/**
 * ARTICLES RAPIDES — LE STUDIO : « le gérant compose lui-même l'écran de vente : des niveaux de tuiles. Ce qu'il
 * dessine ici est ce que la caisse affiche » (maquette, écran `quick`, fichier `Articles rapides - Studio`).
 *
 * Le conteneur : l'en-tête (annuler, état du brouillon, publier), la barre de la disposition (caisses, colonnes,
 * défaut, retour à l'accueil, renvoi), la bascule composer / aperçu caisse, et la vie du BROUILLON.
 *
 * ⚠️ RIEN NE DESCEND AVANT « PUBLIER SUR LES CAISSES » (arbitrage A1) : chaque geste modifie un brouillon enregistré
 *    au SERVEUR — partagé entre les postes, il survit à un navigateur fermé. Sa RÉVISION refuse d'écraser le travail
 *    d'un autre poste : un conflit se DIT et propose de recharger, il ne s'écrase jamais en silence.
 * ⚠️ CRÉER, RENOMMER ET SUPPRIMER UNE DISPOSITION RESTENT IMMÉDIATS : ce sont des gestes d'administration, que le
 *    serveur applique tout de suite ; le brouillon se réaligne sur les dispositions existantes à la lecture.
 * ⚠️ L'ÉCRAN NE MENT PAS SUR L'ENREGISTREMENT : « Non publié » n'est affirmé qu'une fois le brouillon enregistré, et un
 *    échec d'enregistrement s'affiche avec son motif réel.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isPresetChildId, type QuickSourceKind, type QuickTileVisual } from '@orgazone/sync-contract';
import { ApiError } from '@orgazone/api-envelope/client';
import { Button, confirm, EmptyState, Icon, showToast, Tooltip, Trans, useLatest } from '@orgazone/ui-web';
import { PAGE_PAD } from '@orgazone/ui-tokens';
import { api, type RegisterGroup, type StudioArticle, type StudioDocument, type StudioLayout, type StudioSource, type StudioView } from '../api';
import { t } from '../i18n';
import { codeCaisse } from '../caisse';
import { QuickStudioComposer } from './quick-studio-composer';
import { VISUALS } from './quick-studio-look';
import { QuickStudioPreview } from './quick-studio-preview';
import { QuickStudioArticles } from './quick-studio-articles';
import { createDraftSaver, type SaveState } from './quick-studio-save';
import {
  COLS_MAX, COLS_MIN, HISTORY_MAX, addArticles, applyScreenVisual, assignRegisters, defaultLayoutAudience, effectivePath, expandLayout, firstLayoutName, freeRegisters,
  levelArticleIds,
  newTilesVisual, parseSourceRef, presetKindsOf, sameDocument, screenVisualCount, setSettings, sourceRef, sourceRefOf, stripExpanded, toggleRegister,
  withLayout, type PourState,
  type PresetValuesState,
} from './quick-studio-model';

const MONO = 'var(--font-mono)';
/** Délai d'enregistrement du brouillon après le dernier geste — on n'écrit pas au serveur à chaque frappe. */
const SAVE_DELAY_MS = 700;

const messageOf = (e: unknown): string => (e instanceof Error ? e.message : String(e));

export function ArticlesRapides() {
  const [view, setView] = useState<StudioView | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [doc, setDoc] = useState<StudioDocument | null>(null);
  const [history, setHistory] = useState<StudioDocument[]>([]);
  const [articles, setArticles] = useState<Record<string, StudioArticle>>({});
  const [sources, setSources] = useState<Record<string, StudioSource>>({});
  const [pours, setPours] = useState<Record<string, PourState>>({});
  // Les sources dont le contenu est lu ou demandé — une seule lecture chacune, jusqu'au prochain rechargement.
  const pourRequests = useRef(new Set<string>());
  // Les valeurs que les préréglages déploient, par référentiel — même règle : une lecture chacun, un échec reste dit.
  const [presetValues, setPresetValues] = useState<Partial<Record<QuickSourceKind, PresetValuesState>>>({});
  const presetRequests = useRef(new Set<QuickSourceKind>());
  /**
   * LA GÉNÉRATION DES LECTURES DE SOURCES — un rechargement la fait avancer.
   * ⚠️ VIDER LES DEUX ENSEMBLES NE SUFFIT PAS : une lecture partie avant le rechargement revient après lui, et elle
   *    réinstallerait dans l'état neuf le contenu que le rechargement vient justement de déclarer périmé — à côté de la
   *    relecture qu'il a relancée, et en dernier si elle est plus lente.
   */
  const generation = useRef(0);
  const [layoutId, setLayoutId] = useState<string | null>(null);
  const [path, setPath] = useState<string[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [multi, setMulti] = useState<Set<string> | null>(null);
  const [mode, setMode] = useState<'edit' | 'preview'>('edit');
  const [pop, setPop] = useState<'layout' | 'registers' | 'cols' | 'visual' | null>(null);
  const [picker, setPicker] = useState<{ parentId: string | null; rail: boolean } | null>(null);
  const [save, setSave] = useState<SaveState>({ kind: 'idle' });
  const [draftOnServer, setDraftOnServer] = useState(false);
  const [busy, setBusy] = useState(false);

  // ⚠️ LE BROUILLON S'ENREGISTRE HORS DU RENDU, par une file éprouvée à part (`quick-studio-save.ts`) : un état React
  //    lu dans un minuteur serait celui du rendu où il a été posé, et un échec ne doit jamais bloquer les suivants.
  const [saver] = useState(() => createDraftSaver({
    save: (revision, next) => api.saveQuickStudioDraft(revision, next),
    isConflict: (e) => e instanceof ApiError && e.status === 409,
    onState: setSave,
    onSaved: setDraftOnServer,
  }));
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const docRef = useRef<StudioDocument | null>(null);
  docRef.current = doc;
  const historyRef = useRef<StudioDocument[]>([]);
  historyRef.current = history;

  // ⚠️ SEUL LE DERNIER RECHARGEMENT ÉCRIT LE STUDIO : la publication, « Recharger » et « Réessayer » rechargent chacun, et
  //    une réponse plus ancienne revenue en dernier reposerait une RÉVISION dépassée — le prochain enregistrement
  //    tomberait en conflit sur un brouillon que personne d'autre n'a touché.
  const latestLoad = useLatest();
  const load = useCallback(async (keep?: string | null) => {
    const isLatest = latestLoad();
    try {
      const v = await api.quickStudio();
      if (!isLatest()) return;
      const working = v.draft?.document ?? v.published;
      saver.reset(v.revision);
      setView(v); setFailure(null); setDoc(working); setHistory([]); setArticles(v.articles); setSources(v.sources);
      // ⚠️ Un rechargement relit aussi ce que les sources déversent : le catalogue a pu changer depuis la dernière lecture.
      generation.current += 1;
      pourRequests.current.clear(); setPours({});
      presetRequests.current.clear(); setPresetValues({});
      setDraftOnServer(v.draft !== null); setSave({ kind: 'idle' });
      // ⚠️ `?layout=` L'EMPORTE AU PREMIER AFFICHAGE : on arrive ici depuis « Ajouter et organiser » du catalogue,
      //    et ouvrir sur une autre disposition ferait chercher des tuiles qu'on vient de poser.
      const asked = new URLSearchParams(window.location.search).get('layout');
      setLayoutId((prev) => {
        const ids = working?.layouts.map((l) => l.id) ?? [];
        for (const c of [keep, prev, asked]) if (c && ids.includes(c)) return c;
        return working?.defaultLayoutId ?? null;
      });
    } catch (e) { if (isLatest()) setFailure(messageOf(e)); }
  }, [saver, latestLoad]);
  useEffect(() => { void load(); }, [load]);

  /**
   * LA PREMIÈRE DISPOSITION D'UN MAGASIN QUI N'EN A AUCUNE — demandée une fois, puis on relit. Le serveur ne crée rien
   * s'il en existe déjà une (un autre poste l'a posée entre-temps).
   * ⚠️ UNE SEULE DEMANDE PAR OUVERTURE : relancée à chaque rendu, une création qui échoue harcèlerait le serveur. Son
   *    échec reste affiché, et « Réessayer » la relance.
   */
  const [firstFailure, setFirstFailure] = useState<string | null>(null);
  const firstAsked = useRef(false);
  // ⚠️ SEULE LA DERNIÈRE DEMANDE PARLE : un « Réessayer » relance, et l'échec d'une demande dépassée ne doit pas
  //    effacer la réponse de la suivante.
  const latestFirst = useLatest();
  const askFirst = useCallback(() => {
    const isLatest = latestFirst();
    firstAsked.current = true;
    setFirstFailure(null);
    api.ensureFirstQuickLayout(firstLayoutName()).then(
      () => { if (isLatest()) void load(); },
      (e: unknown) => { if (isLatest()) setFirstFailure(messageOf(e)); },
    );
  }, [load, latestFirst]);
  const empty = view !== null && ((view.draft?.document ?? view.published)?.layouts.length ?? 0) === 0;
  useEffect(() => { if (empty && !firstAsked.current) askFirst(); }, [empty, askFirst]);

  /**
   * LES GROUPES DE CAISSES DU MAGASIN (Réglages → Magasin & caisses) — lus ici, jamais écrits : cocher un groupe affecte
   * toutes ses caisses d'un coup. `null` dans la topologie dit « illisible », jamais « aucun groupe » : l'écran le dit.
   * ⚠️ LUS À LA PREMIÈRE OUVERTURE DU MENU « Caisses », une fois : seul ce menu s'en sert.
   */
  const [groups, setGroups] = useState<GroupsState>({ status: 'loading' });
  const groupsAsked = useRef(false);
  const readGroups = useCallback(() => {
    if (groupsAsked.current) return;
    groupsAsked.current = true;
    api.storeTopology().then((topo) => {
      setGroups(topo.groups === null ? { status: 'error', message: t('Les groupes de caisses ne se lisent pas pour le moment.') } : { status: 'ready', groups: topo.groups });
    }, (e: unknown) => setGroups({ status: 'error', message: messageOf(e) }));
  }, []);

  /**
   * LIT CE QUE DES SOURCES DÉVERSENT — une fois chacune : le niveau se range et se compte sur ce contenu, et ses
   * articles rejoignent ceux que l'écran sait dessiner.
   * ⚠️ UN ÉCHEC RESTE AFFICHÉ AVEC SON MOTIF, et il ne se relit pas tout seul : relancer à chaque rendu une lecture qui
   *    échoue harcèlerait le serveur sans rien dire de plus. « Réessayer » la relance (`retryPour`).
   */
  const readPours = useCallback((refs: readonly string[]) => {
    for (const ref of refs) {
      const src = parseSourceRef(ref);
      if (!src || pourRequests.current.has(ref)) continue;
      pourRequests.current.add(ref);
      const asked = generation.current;
      setPours((prev) => ({ ...prev, [ref]: { status: 'loading' } }));
      api.quickStudioPour(src.kind, src.id).then((r) => {
        if (asked !== generation.current) return;
        setArticles((prev) => ({ ...prev, ...Object.fromEntries(r.articles.map((a) => [a.id, a])) }));
        setPours((prev) => ({ ...prev, [ref]: { status: 'ready', ids: r.ids, total: r.total } }));
      }, (e: unknown) => {
        if (asked !== generation.current) return;
        setPours((prev) => ({ ...prev, [ref]: { status: 'error', message: messageOf(e) } }));
      });
    }
  }, []);
  const retryPour = useCallback((ref: string) => { pourRequests.current.delete(ref); readPours([ref]); }, [readPours]);

  /**
   * LIT LES VALEURS D'UN RÉFÉRENTIEL QU'UN PRÉRÉGLAGE DÉPLOIE — celles qui ont des articles en vente, dans l'ordre de la
   * caisse (`GET /quick-studio/sources`). Elles deviennent aussi des sources connues : leurs tuiles déployées se nomment
   * et se comptent sans relire leur contenu.
   */
  const readPresetValues = useCallback((kinds: readonly QuickSourceKind[]) => {
    for (const kind of kinds) {
      if (presetRequests.current.has(kind)) continue;
      presetRequests.current.add(kind);
      const asked = generation.current;
      setPresetValues((prev) => ({ ...prev, [kind]: { status: 'loading' } }));
      api.quickStudioSources(kind).then((r) => {
        if (asked !== generation.current) return;
        setSources((prev) => ({ ...prev, ...Object.fromEntries(r.rows.map((row) => [sourceRef(row.kind, row.id), row])) }));
        setPresetValues((prev) => ({ ...prev, [kind]: { status: 'ready', rows: r.rows } }));
      }, (e: unknown) => {
        if (asked !== generation.current) return;
        setPresetValues((prev) => ({ ...prev, [kind]: { status: 'error', message: messageOf(e) } }));
      });
    }
  }, []);
  const retryPresetValues = useCallback((kind: QuickSourceKind) => { presetRequests.current.delete(kind); readPresetValues([kind]); }, [readPresetValues]);
  const currentLayout = doc ? doc.layouts.find((l) => l.id === layoutId) ?? doc.layouts[0] ?? null : null;
  const presetKinds = currentLayout ? presetKindsOf(currentLayout).join('|') : '';
  // ⚠️ `presetValues` EN DÉPENDANCE, comme pour les sources : un rechargement les vide sans changer les préréglages.
  useEffect(() => {
    if (presetKinds) readPresetValues(presetKinds.split('|') as QuickSourceKind[]);
  }, [presetKinds, readPresetValues, presetValues]);
  const shown = useMemo(() => (currentLayout ? expandLayout(currentLayout, presetValues) : null), [currentLayout, presetValues]);
  const rememberSource = useCallback((s: StudioSource) => {
    setSources((prev) => ({ ...prev, [sourceRef(s.kind, s.id)]: s }));
  }, []);

  /** Enregistre ce qui attend, sans attendre la fin du délai — l'un après l'autre, jamais deux écritures à la même révision. */
  const flush = useCallback((): Promise<void> => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    return saver.flush();
  }, [saver]);
  // ⚠️ QUITTER L'ÉCRAN ENREGISTRE CE QUI ATTEND, tout de suite : un minuteur laissé derrière un écran démonté écrirait
  //    plus tard, pendant que l'utilisateur est déjà ailleurs — et dans le banc, sous l'écran du cas suivant.
  useEffect(() => () => { if (timer.current) void flush().catch(() => undefined); }, [flush]);

  /** Un geste sur le document : empilé pour l'annulation, enregistré au brouillon après un court délai. */
  const change = useCallback((next: StudioDocument, toast?: string) => {
    const before = docRef.current;
    if (!before || sameDocument(before, next)) return;
    setHistory((h) => [...h, before].slice(-HISTORY_MAX));
    setDoc(next);
    saver.set(next);
    if (timer.current) clearTimeout(timer.current);
    // ⚠️ L'ÉCHEC N'EST PAS AVALÉ : la file l'annonce à l'écran (« Brouillon non enregistré : <motif> » + Réessayer) ;
    //    le rejet ne sert qu'à l'appelant qui attend, et un minuteur n'attend personne.
    timer.current = setTimeout(() => { void flush().catch(() => undefined); }, SAVE_DELAY_MS);
    if (toast) showToast(toast, 'ok');
  }, [flush, saver]);

  // ⚠️ LU DANS UNE REF, PAS DANS LA MISE À JOUR D'ÉTAT : React peut rejouer une fonction de mise à jour, et un
  //    enregistrement ou un message posé dedans partirait deux fois.
  const undo = useCallback(() => {
    const h = historyRef.current;
    if (h.length === 0) return;
    const prev = h[h.length - 1]!;
    setHistory(h.slice(0, -1));
    setDoc(prev);
    saver.set(prev);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => { void flush().catch(() => undefined); }, SAVE_DELAY_MS);
    showToast(t('Annulé — {n} pas restants', { n: h.length - 1 }), 'ok');
  }, [flush, saver]);

  // Ctrl+Z annule — jamais depuis un champ de saisie, où il appartient au champ.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      const target = e.target as HTMLElement | null;
      const typing = !!target && (/INPUT|TEXTAREA|SELECT/.test(target.tagName) || target.isContentEditable);
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z' && !typing) { e.preventDefault(); undo(); }
      if (e.key === 'Escape' && !typing) setPop(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo]);

  /** Un geste serveur immédiat : le brouillon en attente part d'abord, puis le geste, puis on relit tout. */
  const serverGesture = useCallback(async (run: () => Promise<string | null>, keep?: string | null) => {
    setBusy(true);
    try {
      await flush();
      const msg = await run();
      if (msg) showToast(msg, 'ok');
      await load(keep);
    } catch (e) {
      showToast(messageOf(e), 'err');
    } finally { setBusy(false); }
  }, [flush, load]);

  if (failure) {
    return (
      <Page>
        <header style={{ padding: '0 0 16px' }}><TitleBlock /></header>
        <EmptyState icon="alert" title={t('Articles rapides illisibles')} note={failure}
          action={<Button onClick={() => { setFailure(null); void load(); }}>{t('Réessayer')}</Button>} />
      </Page>
    );
  }
  if (!view) {
    return (
      <Page>
        <header style={{ padding: '0 0 16px' }}><TitleBlock /></header>
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--ink-400)' }}>{t('Chargement…')}</div>
      </Page>
    );
  }
  if (!doc || doc.layouts.length === 0) {
    // ⚠️ UN STUDIO NEUF N'OUVRE JAMAIS SUR UN VIDE (maquette) : le serveur pose la disposition « Défaut », celle de toutes
    //    les caisses, et la page s'ouvre dessus. Seul son échec s'affiche ici, avec son motif réel.
    return (
      <Page>
        <header style={{ padding: '0 0 16px' }}><TitleBlock /></header>
        {firstFailure ? (
          <EmptyState icon="alert" title={t('La première disposition n’a pas pu être créée')} note={firstFailure}
            action={<Button onClick={askFirst}>{t('Réessayer')}</Button>} />
        ) : (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--ink-400)' }}>{t('Préparation de la disposition…')}</div>
        )}
      </Page>
    );
  }

  const layout: StudioLayout = doc.layouts.find((l) => l.id === layoutId) ?? doc.layouts[0]!;
  const nameOf = (id: string): string => view.layouts.find((l) => l.id === id)?.name ?? id;
  const layoutName = nameOf(layout.id);
  const isDefault = doc.defaultLayoutId === layout.id;
  const free = freeRegisters(doc, view.registers);
  const dirty = draftOnServer || saver.hasPending() || save.kind === 'saving' || save.kind === 'error';
  // ⚠️ CE QUI REVIENT DU COMPOSEUR EST DÉPOUILLÉ DE SES TUILES DÉPLOYÉES : seul le document s'enregistre (`stripExpanded`).
  const setLayout = (next: StudioLayout, toast?: string): void => change(withLayout(doc, layout.id, () => stripExpanded(next)), toast);
  const expanded = shown ?? layout;
  const openLayout = (id: string): void => { setLayoutId(id); setPath([]); setSelected(null); setMulti(null); setPop(null); };

  const publish = async (): Promise<void> => {
    setBusy(true);
    try {
      await flush();
      if (saver.isConflicted()) return;
      const r = await api.publishQuickStudio(saver.revision());
      showToast(r.registersMoved > 0
        ? t('Publié — {n} caisses changent de disposition à leur prochaine synchronisation', { n: r.registersMoved })
        : t('Publié — les caisses reçoivent la disposition à la prochaine synchronisation'), 'ok');
      await load(layout.id);
    } catch (e) {
      showToast(messageOf(e), 'err');
    } finally { setBusy(false); }
  };
  /**
   * PROPAGER LE VISUEL DES NOUVELLES TUILES aux tuiles déjà posées — à toutes, ou aux articles seuls — après une
   * confirmation qui dit combien changent et combien avaient été réglées une à une.
   * ⚠️ LES PHOTOS DÉPOSÉES SONT GARDÉES : repasser une tuile en « Photo » les retrouve.
   */
  const applyVisual = async (scope: 'all' | 'articles'): Promise<void> => {
    const nv = newTilesVisual(layout.settings);
    const name = VISUALS.find((v) => v.key === nv)?.label() ?? '';
    const c = screenVisualCount(shown ?? layout, nv, pours, sources);
    if (!c) return;
    const articles = scope === 'articles';
    const n = articles ? c.art : c.all;
    const own = articles ? c.artOwn : c.allOwn;
    if (n === 0) return;
    const first = articles
      ? (own > 0
        ? t('Les dossiers, familles et marques gardent leur visuel ; {n} article réglé un à un prend aussi celui-ci.', { n: own })
        : t('Les dossiers, familles et marques gardent leur visuel.'))
      : (own > 0
        ? t('Tout l’écran prend ce visuel, y compris {n} tuile réglée une à une.', { n: own })
        : t('Tout l’écran prend ce visuel.'));
    if (!(await confirm({
      // « La tuile » et « les 3 tuiles » (maquette) : UNE tuile se nomme, au-delà la phrase est comptée.
      title: articles
        ? (n === 1 ? t('Passer l’article en « {visuel} » ?', { visuel: name }) : t('Passer les {n} articles en « {visuel} » ?', { n, visuel: name }))
        : (n === 1 ? t('Passer la tuile en « {visuel} » ?', { visuel: name }) : t('Passer les {n} tuiles en « {visuel} » ?', { n, visuel: name })),
      message: `${first} ${t('Les photos déposées sont gardées : repasser une tuile en Photo les retrouve.')}`,
      confirmLabel: articles ? t('Appliquer aux articles') : t('Appliquer à toutes'),
    }))) return;
    change(withLayout(doc, layout.id, (l) => applyScreenVisual(l, scope)), articles
      ? t('{n} article passe en « {visuel} »', { n, visuel: name })
      : t('{n} tuile passe en « {visuel} »', { n, visuel: name }));
  };
  const discard = async (): Promise<void> => {
    if (!(await confirm({
      title: t('Abandonner le brouillon ?'),
      message: t('Toutes les modifications non publiées sont perdues. Le studio repart de ce que les caisses ont aujourd’hui.'),
      confirmLabel: t('Abandonner'),
    }))) return;
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    saver.set(null);
    await serverGesture(async () => {
      await saver.settled();
      await api.discardQuickStudioDraft(saver.revision());
      return t('Brouillon abandonné.');
    }, layout.id);
  };
  /**
   * SUPPRIMER UNE DISPOSITION depuis sa ligne — jamais la dernière (sa corbeille n'existe pas). Ses caisses repassent sur
   * la défaut ; supprimer la défaut fait de la PREMIÈRE restante la nouvelle défaut, et c'est elle que la confirmation
   * nomme puis que le serveur reçoit (maquette `layDelete`).
   * ⚠️ LA DÉFAUT QUI COMPTE EST CELLE QUE LES CAISSES ONT (publiée) : c'est elle que la suppression, immédiate, déplace.
   */
  const removeLayout = async (id: string): Promise<void> => {
    const target = doc.layouts.find((l) => l.id === id);
    const others = doc.layouts.filter((l) => l.id !== id);
    const publishedDefault = view.layouts.find((l) => l.isDefault)?.id ?? doc.defaultLayoutId;
    const wasDefault = publishedDefault === id;
    const heir = wasDefault ? others[0] : others.find((l) => l.id === publishedDefault) ?? others[0];
    if (!target || !heir) return;
    const said = [
      target.registers.length > 0
        ? t('{n} caisse ({liste}) repasse sur « {nom} ».', { n: target.registers.length, liste: target.registers.map(codeCaisse).join(', '), nom: nameOf(heir.id) })
        : null,
      wasDefault ? t('« {nom} » devient la disposition par défaut.', { nom: nameOf(heir.id) }) : null,
      t('Son écran d’accueil et ses réglages disparaissent des caisses à leur prochaine synchronisation. Le catalogue n’est pas touché.'),
    ].filter((x): x is string => x !== null);
    if (!(await confirm({ title: t('Supprimer « {nom} » ?', { nom: nameOf(id) }), message: said.join(' '), confirmLabel: t('Supprimer') }))) return;
    const name = nameOf(id);
    await serverGesture(async () => {
      await api.deleteQuickLayout(id, wasDefault ? heir.id : null);
      return t('« {nom} » supprimée', { nom: name });
    }, layout.id === id ? heir.id : layout.id);
  };
  const resend = async (): Promise<void> => {
    if (!(await confirm({
      title: t('Renvoyer « {nom} » aux caisses ?', { nom: layoutName }),
      message: t('Les caisses reçoivent de nouveau la disposition PUBLIÉE, et les déplacements de tuiles faits en caisse sont effacés. Le brouillon, lui, n’est pas publié.'),
      confirmLabel: t('Renvoyer'), tone: 'info',
    }))) return;
    await serverGesture(async () => {
      const r = await api.republishQuickLayout(layout.id);
      return r.generation === null
        ? t('Renvoyée — {n} lignes réémises. Cette disposition n’a pas encore été publiée depuis le studio.', { n: r.republished })
        : t('Renvoyée — {n} lignes réémises, les déplacements faits en caisse seront effacés.', { n: r.republished });
    }, layout.id);
  };

  const composerPath = effectivePath(expanded, path);

  // ⚠️ LA BARRE « DISPOSITION » VIT DANS LA COLONNE DU CANEVAS, au-dessus de lui (maquette) : l'inspecteur commence à sa
  //    hauteur, dans la colonne de droite. En aperçu elle disparaît, et la tablette suit directement l'en-tête.
  const dispositionBar = (
    <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '9px 12px', marginBottom: 12, background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)' }}>
      <span style={{ fontFamily: MONO, fontSize: 'var(--t-micro)', letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--ink-500)' }}>{t('Disposition')}</span>
      <span style={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
        <button type="button" onClick={() => setPop(pop === 'layout' ? null : 'layout')}
          style={{ height: 34, padding: '0 11px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', display: 'inline-flex', alignItems: 'center', gap: 8, maxWidth: 260 }}>
          <LayoutDot shown={layout.registers.length > 0 || isDefault} />
          <span style={{ fontSize: 'var(--t-lead)', fontWeight: 700, color: 'var(--ink-900)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{layoutName}</span>
          <Icon name="chevronD" size={12} stroke={2.4} color="var(--ink-400)" style={{ flexShrink: 0 }} />
        </button>
        {pop === 'layout' && (
          <LayoutMenu
            doc={doc} names={nameOf} current={layout.id} free={free} busy={busy} onClose={() => setPop(null)} onPick={openLayout}
            onCreate={(name) => serverGesture(async () => {
              const l = await api.createQuickLayout(name);
              setLayoutId(l.id);
              return t('Disposition créée — affectez-lui ses caisses.');
            })}
            onRename={(name) => serverGesture(async () => { await api.renameQuickLayout(layout.id, name); return t('Disposition renommée.'); }, layout.id)}
            onDelete={(id) => { setPop(null); void removeLayout(id); }}
          />
        )}
      </span>
      <span style={{ width: 1, alignSelf: 'stretch', background: 'var(--ink-200)' }} />
      {/* Les caisses de la disposition, puis le bouton qui les règle — collés, comme la maquette ; les réglages de la grille
          vivent à DROITE du bandeau, et passent à la ligne sur un petit écran, toujours calés à droite. */}
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, flexWrap: 'wrap', flex: '0 1 auto', minWidth: 0 }}>
        {layout.registers.map((r) => <Chip key={r} kind="register">{codeCaisse(r)}</Chip>)}
        {/* ⚠️ LA DÉFAUT SANS CAISSE AFFECTÉE N'EST PAS « SANS CAISSE » : toute caisse non affectée l'affiche. */}
        {layout.registers.length === 0 && !isDefault && <span style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-400)' }}>{t('Aucune caisse')}</span>}
        {layout.registers.length === 0 && isDefault && <Chip kind="all">{t('Toutes les caisses')}</Chip>}
        {isDefault && <Chip kind="default">{t('PAR DÉFAUT')}</Chip>}
        <span style={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
          <button type="button" onClick={() => { if (pop !== 'registers') readGroups(); setPop(pop === 'registers' ? null : 'registers'); }} aria-expanded={pop === 'registers'}
            style={{ height: 34, padding: '0 12px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 'var(--t-body)', fontWeight: 600, color: 'var(--ink-800)' }}>
            <Icon name="monitor" size={14} />{t('Caisses')}
          </button>
          {pop === 'registers' && (
            <RegistersMenu
              doc={doc} layout={layout} names={nameOf} registers={view.registers} free={free} busy={busy} groups={groups}
              onClose={() => setPop(null)}
              onToggle={(r) => {
                const res = toggleRegister(doc, layout.id, r);
                change(res.doc, res.removed
                  ? t('{caisse} retirée — elle affichera la disposition par défaut', { caisse: codeCaisse(r) })
                  : res.from
                    ? t('{caisse} quitte « {avant} » pour « {apres} »', { caisse: codeCaisse(r), avant: nameOf(res.from), apres: layoutName })
                    : t('{caisse} affectée à « {nom} »', { caisse: codeCaisse(r), nom: layoutName }));
              }}
              onGroup={(g) => {
                const all = g.registerIds.every((r) => layout.registers.includes(r));
                change(assignRegisters(doc, layout.id, g.registerIds, !all), all
                  ? t('Groupe « {nom} » retiré — ses caisses repassent sur la disposition par défaut', { nom: g.name })
                  : t('Groupe « {nom} » affecté — {liste}', { nom: g.name, liste: g.registerIds.map(codeCaisse).join(', ') }));
              }}
              onDefault={() => { if (!isDefault) change({ ...doc, defaultLayoutId: layout.id }, t('« {nom} » devient la disposition par défaut', { nom: layoutName })); }}
              onHome={() => change(setSettings(doc, layout.id, { home: !layout.settings.home }))}
              onResend={() => { setPop(null); void resend(); }}
            />
          )}
        </span>
      </span>
      <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: '10px 14px', flexWrap: 'wrap', justifyContent: 'flex-end', minWidth: 0 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, flexShrink: 0 }}>
          <span style={{ fontFamily: MONO, fontSize: 'var(--t-micro)', letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--ink-500)' }}>{t('Colonnes')}</span>
          <span style={{ position: 'relative', display: 'inline-flex' }}>
            <button type="button" onClick={() => setPop(pop === 'cols' ? null : 'cols')} aria-expanded={pop === 'cols'}
              style={{ height: 32, minWidth: 128, padding: '0 10px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', display: 'inline-flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, fontSize: 'var(--t-body)', fontWeight: 600, color: 'var(--ink-900)' }}>
              {layout.settings.cols ? t('{n} colonnes max', { n: layout.settings.cols }) : t('Colonnes auto')}
              <Icon name="chevronD" size={12} stroke={2.4} color="var(--ink-400)" />
            </button>
            {pop === 'cols' && (
              <>
                <span onClick={() => setPop(null)} style={{ position: 'fixed', inset: 0, zIndex: 598 }} />
                <div style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 600, width: 236, maxHeight: 320, overflowY: 'auto', background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', boxShadow: 'var(--sh-popover)', padding: 5 }}>
                  {/* ⚠️ LE NOMBRE EST UN MAXIMUM (`quickGridColumns`) : sur un écran étroit, la caisse en affiche moins plutôt
                      que de rétrécir les tuiles jusqu'à « une icône et deux lettres ». Chaque choix le dit.
                      ⚠️ SANS CHIFFRE : la taille d'une tuile dépend de ce que porte CHAQUE niveau (photo, grand ou petit
                      picto, texte seul — `quickDrivingClass`), pas de la disposition ; « 150 px » n'était vrai que pour
                      un niveau à photo. */}
                  {[null, ...Array.from({ length: COLS_MAX - COLS_MIN + 1 }, (_, i) => COLS_MIN + i)].map((n) => {
                    const on = layout.settings.cols === n;
                    return (
                      <button key={n ?? 'auto'} type="button" aria-pressed={on}
                        onClick={() => { change(setSettings(doc, layout.id, { cols: n })); setPop(null); }}
                        style={{ display: 'flex', alignItems: 'center', gap: 9, width: '100%', padding: '7px 9px', border: 'none', borderRadius: 'var(--r-xs)', background: on ? 'var(--teal-100)' : 'transparent', textAlign: 'left' }}>
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ display: 'block', fontSize: 'var(--t-body)', fontWeight: 600, color: on ? 'var(--teal-800)' : 'var(--ink-800)' }}>
                            {n === null ? t('Automatique') : t('Jusqu’à {n} colonnes', { n })}
                          </span>
                          <span style={{ display: 'block', fontSize: 'var(--t-meta)', color: 'var(--ink-500)', marginTop: 1 }}>
                            {n === null
                              ? t('Autant de tuiles que l’écran en tient, à leur taille')
                              : t('Moins sur un écran étroit — une tuile ne passe jamais sous sa taille minimale')}
                          </span>
                        </span>
                        {on && <Icon name="check" size={13} stroke={3} color="var(--teal-600)" style={{ flexShrink: 0 }} />}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </span>
        </span>
        {/* NOUVELLES TUILES — choisir le visuel des tuiles qu'on AJOUTERA ne touche aucune tuile posée ; propager est un
            second geste, confirmé (`NewTilesMenu`). */}
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, flexShrink: 0 }}>
          <span style={{ fontFamily: MONO, fontSize: 'var(--t-micro)', letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--ink-500)' }}>{t('Nouvelles tuiles')}</span>
          <span style={{ position: 'relative', display: 'inline-flex' }}>
            <button type="button" onClick={() => setPop(pop === 'visual' ? null : 'visual')} aria-expanded={pop === 'visual'}
              title={t('Le visuel des tuiles que vous ajouterez — celles déjà posées ne changent pas')}
              style={{ height: 32, minWidth: 138, padding: '0 10px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', display: 'inline-flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap', fontSize: 'var(--t-body)', fontWeight: 600, color: 'var(--ink-900)' }}>
              <span style={{ display: 'inline-flex', color: 'var(--teal-700)' }}><Icon name={VISUALS.find((v) => v.key === newTilesVisual(layout.settings))?.icon ?? 'tag'} size={14} stroke={1.9} /></span>
              <span style={{ flex: 1, textAlign: 'left' }}>{VISUALS.find((v) => v.key === newTilesVisual(layout.settings))?.label()}</span>
              <span style={{ display: 'inline-flex', color: 'var(--ink-400)' }}><Icon name="chevronD" size={12} stroke={2.4} /></span>
            </button>
            {pop === 'visual' && (
              <NewTilesMenu layout={shown ?? layout} pours={pours} sources={sources} onNeedPours={readPours} onClose={() => setPop(null)}
                onPick={(v) => change(setSettings(doc, layout.id, { newVisual: v }),
                  t('Nouvelles tuiles en « {visuel} » — rien ne change sur l’écran', { visuel: VISUALS.find((x) => x.key === v)?.label() ?? '' }))}
                onApply={(scope) => { setPop(null); void applyVisual(scope); }} />
            )}
          </span>
        </span>
      </span>
    </div>
  );
  const present = levelArticleIds(expanded, picker?.parentId ?? null, pours);

  return (
    <Page>
      <header style={{ padding: '0 0 16px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 280 }}><TitleBlock /></div>
          <button type="button" onClick={undo} disabled={history.length === 0} aria-label={t('Annuler la dernière modification')}
            title={history.length ? t('Annuler la dernière modification (Ctrl+Z)') : t('Rien à annuler')}
            style={{ height: 40, width: 40, border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-700)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', position: 'relative', opacity: history.length ? 1 : 0.4 }}>
            <Icon name="undo" size={16} />
            {history.length > 0 && (
              <span style={{ position: 'absolute', top: -6, right: -6, minWidth: 17, height: 17, padding: '0 4px', borderRadius: 'var(--r-pill)', background: 'var(--ink-900)', color: 'var(--paper)', fontSize: 'var(--t-micro)', fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{history.length}</span>
            )}
          </button>
          <SaveChip save={save} dirty={dirty} onRetry={() => { void flush().catch(() => undefined); }} onDiscard={() => { void discard(); }} />
          {/* ⚠️ LE BOUTON DE LA MAQUETTE DU STUDIO, PAS LE CTA DE LA CHARTE : un aplat de la marque, 40 px, son ombre
              teintée. ⚠️ INACTIF QUAND IL N'Y A RIEN À PUBLIER, là où la maquette le laisse toujours actif : publier une
              composition que les caisses ont déjà ne ferait rien, et un bouton qui fait semblant ment (§23.0). */}
          <Button variant="primary" size="lg" disabled={busy || !dirty || save.kind === 'conflict'}
            reason={!dirty ? t('Rien à publier : les caisses ont déjà cette composition.') : undefined}
            onClick={() => { void publish(); }}
            style={{ padding: '0 17px', gap: 8, fontWeight: 700, border: 'none', borderRadius: 'var(--r-ctrl)', background: 'var(--teal-500)', color: '#fff', boxShadow: '0 6px 18px -8px var(--teal-500)' }}>
            <Icon name="arrowT" size={15} stroke={2.2} />
            {t('Publier sur les caisses')}
          </Button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginTop: 16 }}>
          <span style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-500)', maxWidth: 520, lineHeight: 1.45 }}>
            {t('Des tuiles qui s’emboîtent : un dossier ouvre ses familles, une famille déverse ses articles. Profondeur libre.')}
          </span>
          <span style={{ flex: 1 }} />
          <span style={{ display: 'inline-flex', padding: 3, gap: 3, borderRadius: 'var(--r-ctrl)', background: 'var(--ink-100)', border: '1px solid var(--ink-200)' }}>
            {(['edit', 'preview'] as const).map((m) => (
              // ⚠️ `aria-pressed` : le mode actif ne se disait que par la couleur du bouton — invisible pour une aide technique.
              <button key={m} type="button" aria-pressed={mode === m} onClick={() => setMode(m)}
                style={{ height: 32, padding: '0 13px', border: 'none', borderRadius: 'var(--r-xs)', background: mode === m ? 'var(--paper)' : 'transparent', color: mode === m ? 'var(--ink-900)' : 'var(--ink-500)', boxShadow: mode === m ? '0 1px 4px rgba(12,14,16,.14)' : 'none', fontSize: 'var(--t-body)', fontWeight: 600 }}>
                {m === 'edit' ? t('Composer') : t('Aperçu caisse')}
              </button>
            ))}
          </span>
        </div>
      </header>

      {save.kind === 'conflict' && (
        <Banner tone="amber" action={<Button onClick={() => { void load(layout.id); }}>{t('Recharger')}</Button>}>
          {t('Le brouillon a changé depuis un autre poste : vos dernières modifications ne sont pas enregistrées. Rechargez pour reprendre la version enregistrée.')}
        </Banner>
      )}
      {view.draftUnreadable && (
        <Banner tone="rose" action={<Button onClick={() => { void discard(); }}>{t('Abandonner le brouillon')}</Button>}>
          {view.draftUnreadable}
        </Banner>
      )}
      {view.defaultMissing && (
        <Banner tone="amber">{t('Aucune disposition n’est publiée par défaut : « {nom} » le deviendra à la publication.', { nom: nameOf(doc.defaultLayoutId) })}</Banner>
      )}

      {mode === 'edit' ? (
        <QuickStudioComposer
          context={dispositionBar}
          layout={expanded} articles={articles} sources={sources} pours={pours}
          onNeedPours={readPours} onRetryPour={retryPour} onSource={rememberSource}
          presetValues={presetValues} onRetryPresetValues={retryPresetValues}
          path={path} onPath={setPath}
          selected={selected} onSelect={setSelected} multi={multi} onMulti={setMulti}
          onLayout={setLayout} disabled={busy || save.kind === 'conflict'}
          onPickArticles={(parentId, rail) => setPicker({ parentId, rail })}
        />
      ) : (
        <QuickStudioPreview
          layout={expanded} layoutName={layoutName} articles={articles} sources={sources} pours={pours} presetValues={presetValues}
          onNeedPours={readPours} onRetryPour={retryPour} startPath={composerPath}
        />
      )}

      {picker && (
        <QuickStudioArticles
          title={picker.rail ? t('Ajouter des articles au menu fixe') : t('Ajouter des articles')}
          present={present}
          onClose={() => setPicker(null)}
          onAdd={(list) => {
            setArticles((prev) => ({ ...prev, ...Object.fromEntries(list.map((a) => [a.id, a])) }));
            const r = addArticles(layout, picker.parentId, list.map((a) => a.id));
            setLayout(r.layout, r.added.length > 0
              ? (r.skipped > 0 ? t('{n} articles ajoutés · {deja}', { n: r.added.length, deja: t('{n} déjà présents', { n: r.skipped }) }) : t('{n} articles ajoutés', { n: r.added.length }))
              : t('Déjà tous présents dans ce niveau'));
            if (r.added.length === 1) setSelected(r.added[0]!);
            setPicker(null);
          }}
        />
      )}
    </Page>
  );
}

/** L'état du brouillon, à côté de « Publier » : c'est là qu'on décide si ce qu'on a fait part aux caisses. */
function SaveChip({ save, dirty, onRetry, onDiscard }: { save: SaveState; dirty: boolean; onRetry: () => void; onDiscard: () => void }) {
  if (save.kind === 'error') {
    return (
      <span role="alert" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, height: 40, padding: '0 12px', borderRadius: 'var(--r-ctrl)', background: 'var(--rose-bg)', color: 'var(--rose-fg)', fontSize: 'var(--t-body)', fontWeight: 600, maxWidth: 380 }}>
        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t('Brouillon non enregistré : {motif}', { motif: save.message })}</span>
        <button type="button" onClick={onRetry} style={{ border: 'none', background: 'transparent', color: 'inherit', textDecoration: 'underline', fontWeight: 700, padding: 0 }}>{t('Réessayer')}</button>
      </span>
    );
  }
  if (!dirty) return null;
  const saving = save.kind === 'saving';
  return (
    <span title={saving ? undefined : t('Le brouillon est enregistré sur le serveur — il partira aux caisses à la publication.')}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 8, height: 40, padding: '0 12px', borderRadius: 'var(--r-ctrl)', background: 'var(--amber-bg)', color: 'var(--amber-fg)', fontSize: 'var(--t-body)', fontWeight: 600 }}>
      <span style={{ width: 7, height: 7, borderRadius: 'var(--r-pill)', background: 'var(--amber)' }} />
      {saving ? t('Enregistrement…') : t('Non publié')}
      {!saving && (
        <button type="button" onClick={onDiscard} style={{ border: 'none', background: 'transparent', color: 'inherit', textDecoration: 'underline', fontWeight: 600, padding: 0, fontSize: 'var(--t-dense)' }}>{t('Abandonner')}</button>
      )}
    </span>
  );
}

/**
 * Le menu des dispositions : en choisir une, en supprimer une depuis sa ligne, renommer la courante, en créer une (un
 * nom est exigé).
 * ⚠️ LA CORBEILLE N'EXISTE QUE S'IL EN RESTE AU MOINS DEUX : la dernière est celle que prend toute caisse.
 */
/** Ce que montre chaque visuel des nouvelles tuiles, sous le mot de la maquette. */
const NEW_VISUAL_HINTS: Record<QuickTileVisual, () => string> = {
  none: () => t('Petit carré : le nom et son contenu'),
  icon: () => t('Carré : petit picto, puis le nom et son contenu'),
  large_icon: () => t('Grand carré : grand picto, puis le nom et son contenu'),
  photo: () => t('Bandeau photo, à déposer tuile par tuile'),
};

/**
 * LES « NOUVELLES TUILES » — le visuel que prendront les tuiles qu'on AJOUTE, et, à part, sa propagation aux tuiles déjà
 * posées.
 * ⚠️ CHOISIR NE TOUCHE AUCUNE TUILE POSÉE : propager est un second geste, CONFIRMÉ — un écran retouché à la main n'est
 *    jamais écrasé en douce.
 * ⚠️ LES DEUX BOUTONS RESTENT LÀ, GRISÉS quand ils n'ont rien à changer, et l'infobulle dit pourquoi (maquette) : un bouton
 *    qui disparaît fait croire que la fonction a été retirée.
 */
function NewTilesMenu({ layout, pours, sources, onNeedPours, onClose, onPick, onApply }: {
  /** La disposition DÉPLOYÉE : ce que ses préréglages génèrent compte aussi. */
  layout: StudioLayout;
  pours: Record<string, PourState>;
  sources: Record<string, StudioSource>;
  /** Demande le contenu de ces sources — le compte ne se donne qu'entier. */
  onNeedPours: (refs: string[]) => void;
  onClose: () => void;
  onPick: (visual: QuickTileVisual) => void;
  onApply: (scope: 'all' | 'articles') => void;
}) {
  const nv = newTilesVisual(layout.settings);
  const name = VISUALS.find((v) => v.key === nv)?.label() ?? '';
  // ⚠️ CE QUE DÉVERSENT LES SOURCES CHANGE AUSSI D'ASPECT : on les lit toutes à l'ouverture, et tant que l'une manque le
  //    compte se tait plutôt que d'annoncer moins de tuiles qu'il n'en change.
  const refs = layout.tiles.filter((x) => !isPresetChildId(x.id)).map(sourceRefOf).filter((r): r is string => r !== null).sort().join('|');
  useEffect(() => { if (refs) onNeedPours(refs.split('|')); }, [refs, onNeedPours]);
  const c = screenVisualCount(layout, nv, pours, sources);
  const hint = c === null
    ? t('Lecture de ce que déversent les familles et les marques…')
    : c.all === 0
    ? t('Toutes les tuiles de cet écran sont déjà en « {visuel} ».', { visuel: name })
    : c.allOwn > 0
      ? t('Ce choix ne modifie aucune tuile déjà posée : {n} n’est pas en « {visuel} » (dont {reglees}).', { n: c.all, visuel: name, reglees: t('{n} réglée une à une', { n: c.allOwn }) })
      : t('Ce choix ne modifie aucune tuile déjà posée : {n} n’est pas en « {visuel} ».', { n: c.all, visuel: name });
  return (
    <>
      <span onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 598 }} />
      <div role="dialog" aria-label={t('Visuel des nouvelles tuiles')} style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 600, width: 296, maxWidth: 'calc(100vw - 24px)', background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', boxShadow: 'var(--sh-popover)', overflow: 'hidden' }}>
        <div style={{ padding: '10px 12px 4px', fontFamily: MONO, fontSize: 'var(--t-eyebrow)', letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--ink-500)' }}>{t('Visuel des nouvelles tuiles')}</div>
        <div style={{ padding: '2px 5px 6px' }}>
          {VISUALS.map((v) => {
            const on = v.key === nv;
            return (
              <button key={v.key} type="button" aria-pressed={on} onClick={() => { if (!on) onPick(v.key); }}
                style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '7px 8px', border: 'none', borderRadius: 'var(--r-xs)', background: on ? 'var(--teal-100)' : 'transparent', textAlign: 'left' }}>
                <span aria-hidden style={{ width: 30, height: 30, borderRadius: 'var(--r-xs)', background: on ? 'var(--paper)' : 'var(--ink-100)', color: on ? 'var(--teal-700)' : 'var(--ink-600)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Icon name={v.icon} size={v.menuSize} stroke={1.9} />
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 'var(--t-body)', fontWeight: 600, color: on ? 'var(--teal-800)' : 'var(--ink-800)' }}>{v.label()}</span>
                  <span style={{ display: 'block', fontSize: 'var(--t-meta)', color: 'var(--ink-500)', marginTop: 1 }}>{NEW_VISUAL_HINTS[v.key]()}</span>
                </span>
                {on && <Icon name="check" size={13} stroke={3} color="var(--teal-600)" style={{ flexShrink: 0 }} />}
              </button>
            );
          })}
        </div>
        <div style={{ padding: '10px 12px 12px', borderTop: '1px solid var(--ink-100)', background: 'var(--ink-50)', display: 'flex', flexDirection: 'column', gap: 7 }}>
          <span style={{ fontFamily: MONO, fontSize: 'var(--t-eyebrow)', letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--ink-500)' }}>{t('Tuiles déjà posées')}</span>
          <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink-600)', lineHeight: 1.45, textWrap: 'pretty' }}>{hint}</span>
          {c !== null && (
            <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <Button size="sm" variant="primary" disabled={c.all === 0} onClick={() => onApply('all')}
                reason={c.all === 0 ? t('Toutes les tuiles sont déjà en « {visuel} »', { visuel: name }) : undefined}
                style={{ padding: '0 12px', fontSize: 'var(--t-dense)', fontWeight: 700, border: 'none', borderRadius: 'var(--r-ctrl)', background: 'var(--teal-500)', color: '#fff', boxShadow: 'none', opacity: c.all === 0 ? 0.45 : 1 }}>
                {t('Appliquer à toutes les tuiles…')}
              </Button>
              <Button size="sm" disabled={c.art === 0} onClick={() => onApply('articles')}
                reason={c.art === 0 ? t('Tous les articles sont déjà en « {visuel} »', { visuel: name }) : undefined}
                style={{ padding: '0 11px', fontSize: 'var(--t-dense)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-700)', opacity: c.art === 0 ? 0.45 : 1 }}>
                {t('Aux articles seulement…')}
              </Button>
            </span>
          )}
        </div>
      </div>
    </>
  );
}

function LayoutMenu({ doc, names, current, free, busy, onClose, onPick, onCreate, onRename, onDelete }: {
  doc: StudioDocument; names: (id: string) => string; current: string;
  /** Les caisses qu'aucune disposition ne prend — nommées sous la disposition par défaut, qui les sert. */
  free: string[]; busy: boolean;
  onClose: () => void; onPick: (id: string) => void; onCreate: (name: string) => void; onRename: (name: string) => void; onDelete: (id: string) => void;
}) {
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  /** Le nom en cours de saisie de la disposition courante — `null` tant qu'on ne la renomme pas. */
  const [rename, setRename] = useState<string | null>(null);
  /** Enregistre le nom saisi s'il a changé, et referme le champ. */
  const commitRename = (): void => {
    const v = (rename ?? '').trim();
    if (v && v !== names(current)) onRename(v);
    setRename(null);
  };
  const deletable = doc.layouts.length > 1;
  return (
    <>
      <span onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 38 }} />
      <div style={{ position: 'absolute', top: 40, left: 0, zIndex: 40, width: 280, background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', boxShadow: 'var(--sh-popover)', overflow: 'hidden' }}>
        <div style={menuTitle}>{t('Dispositions')}</div>
        <div style={{ padding: '4px 8px 8px', display: 'flex', flexDirection: 'column', gap: 3 }}>
          {doc.layouts.map((l) => {
            const on = l.id === current;
            const def = doc.defaultLayoutId === l.id;
            const regs = l.registers.map(codeCaisse).join(' · ');
            const renaming = on && rename !== null;
            const rowStyle: React.CSSProperties = { flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 9, padding: '9px 0 9px 10px', border: 'none', background: 'transparent', textAlign: 'left' };
            const body = (
              <>
                <LayoutDot shown={l.registers.length > 0 || def} />
                <span style={{ flex: 1, minWidth: 0 }}>
                  {renaming ? (
                    <input autoFocus value={rename} disabled={busy} aria-label={t('Nouveau nom de « {nom} »', { nom: names(l.id) })} maxLength={60}
                      onChange={(e) => setRename(e.target.value)} onBlur={commitRename}
                      onKeyDown={(e) => { if (e.key === 'Enter') commitRename(); if (e.key === 'Escape') { e.stopPropagation(); setRename(null); } }}
                      style={{ display: 'block', width: '100%', height: 18, padding: 0, border: 'none', borderBottom: '1px solid var(--teal-400)', background: 'transparent', fontSize: 'var(--t-body)', fontWeight: 600, color: 'var(--ink-900)', outline: 'none' }} />
                  ) : (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                      <span style={{ minWidth: 0, fontSize: 'var(--t-body)', fontWeight: 600, color: 'var(--ink-900)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{names(l.id)}</span>
                      {def && (
                        <Tooltip content={t('Affichée par toute caisse non affectée')}>
                          <span style={{
                            height: 18, display: 'inline-flex', alignItems: 'center', padding: '0 6px', borderRadius: 'var(--r-pill)', flexShrink: 0,
                            background: 'var(--ink-100)', color: 'var(--ink-600)', fontSize: '9.5px', fontWeight: 800, letterSpacing: '.04em', whiteSpace: 'nowrap',
                          }}>{t('Par défaut')}</span>
                        </Tooltip>
                      )}
                    </span>
                  )}
                  <span style={{ display: 'block', fontSize: 'var(--t-sub)', color: 'var(--ink-500)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {/* La défaut NOMME les caisses qu'elle prend sans qu'on les lui ait attribuées : celles qu'aucune autre ne prend. */}
                    {def ? defaultLayoutAudience(l.registers, free) : (regs || t('aucune caisse'))}
                  </span>
                </span>
              </>
            );
            return (
              <div key={l.id} className="oz-qs-choice" data-on={on ? '' : undefined}
                style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '0 6px 0 0', borderRadius: 'var(--r-ctrl)', background: on ? 'var(--teal-100)' : 'var(--paper)' }}>
                {/* ⚠️ RENOMMER : un clic sur la disposition COURANTE ouvre son nom à la saisie. La maquette n'a nulle part où renommer
                    une disposition, et sans ce geste une « Nouvelle disposition » garderait à jamais son premier nom ; un crayon
                    sur la ligne en aurait tronqué le sous-titre. Pendant la saisie, la ligne n'est plus un bouton : un champ ne
                    se pose pas dans un bouton. */}
                {renaming
                  ? <div style={rowStyle}>{body}</div>
                  : (
                    <button type="button" onClick={() => (on ? setRename(names(l.id)) : onPick(l.id))} aria-current={on || undefined}
                      title={on ? t('Renommer la disposition') : undefined} style={rowStyle}>{body}</button>
                  )}
                {deletable && (
                  <button type="button" className="oz-qs-layout-del" disabled={busy} onClick={() => onDelete(l.id)}
                    aria-label={t('Supprimer la disposition « {nom} »', { nom: names(l.id) })} title={t('Supprimer la disposition')}
                    style={{ width: 26, height: 26, flexShrink: 0, border: 'none', borderRadius: 'var(--r-xs)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
                    <Icon name="trash" size={13} stroke={2} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
        {creating ? (
          <div style={{ display: 'flex', gap: 6, padding: '8px 12px 12px', borderTop: '1px solid var(--ink-200)', background: 'var(--ink-50)' }}>
            <input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder={t('Comptoir, Corner, Réserve…')} aria-label={t('Nom de la nouvelle disposition')}
              onKeyDown={(e) => { if (e.key === 'Enter' && name.trim()) onCreate(name.trim()); if (e.key === 'Escape') setCreating(false); }}
              style={{ flex: 1, minWidth: 0, height: 32, padding: '0 10px', border: '1px solid var(--teal-300)', borderRadius: 'var(--r-ctrl)', fontSize: 'var(--t-body)' }} />
            <Button size="sm" variant="primary" disabled={!name.trim() || busy} onClick={() => onCreate(name.trim())}>{t('Créer')}</Button>
          </div>
        ) : (
          <button type="button" className="oz-qs-layout-new" onClick={() => setCreating(true)}
            style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', height: 38, padding: '0 14px', border: 'none', borderTop: '1px solid var(--ink-200)', color: 'var(--teal-800)', fontSize: 'var(--t-body)', fontWeight: 700, textAlign: 'left' }}>
            <Icon name="plus" size={14} stroke={2.4} />{t('Nouvelle disposition')}
          </button>
        )}
      </div>
    </>
  );
}

/** L'état de lecture des groupes de caisses — lus une fois, l'échec dit avec son motif. */
type GroupsState = { status: 'loading' } | { status: 'ready'; groups: RegisterGroup[] } | { status: 'error'; message: string };

/**
 * Le menu des caisses de la disposition courante : les GROUPES de caisses (cocher un groupe affecte toutes ses caisses),
 * les caisses une à une, la disposition par défaut, le retour à l'accueil et le renvoi.
 * ⚠️ UN GROUPE À MOITIÉ ICI SE DIT (case à tiret, « 1 sur 2 ici ») : cocher le complète, décocher le retire en entier.
 */
function RegistersMenu({ doc, layout, names, registers, free, busy, groups, onClose, onToggle, onGroup, onDefault, onHome, onResend }: {
  doc: StudioDocument; layout: StudioLayout; names: (id: string) => string; registers: string[]; free: string[]; busy: boolean;
  groups: GroupsState; onClose: () => void; onToggle: (r: string) => void; onGroup: (g: RegisterGroup) => void;
  onDefault: () => void; onHome: () => void; onResend: () => void;
}) {
  const isDefault = doc.defaultLayoutId === layout.id;
  const ownerOf = (r: string): StudioLayout | undefined => doc.layouts.find((l) => l.registers.includes(r));
  // Un groupe sans caisse n'affecte rien : il ne s'offre pas.
  const shownGroups = groups.status === 'ready' ? groups.groups.filter((g) => g.registerIds.length > 0) : [];
  return (
    <>
      <span onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 38 }} />
      <div style={{ position: 'absolute', top: 40, right: 0, zIndex: 40, width: 300, background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', boxShadow: 'var(--sh-popover)', overflow: 'hidden' }}>
        <div style={{ ...menuTitle, padding: '10px 14px 2px' }}>{t('Caisses')}</div>
        <div style={{ padding: '0 14px 8px', fontSize: 'var(--t-note)', color: 'var(--ink-500)', lineHeight: 1.45 }}>
          {t('Une caisse n’affiche qu’une disposition : en cocher une déjà prise la déplace ici. Un groupe coche toutes ses caisses d’un coup.')}
        </div>
        {/* ⚠️ SANS HAUTEUR BORNÉE (maquette) : la liste défilait dès cinq caisses, et sa barre de défilement mangeait 10 px à
            chaque ligne. Un magasin de vingt caisses fait défiler la page, pas le menu. */}
        <div style={{ padding: '0 8px 8px', display: 'flex', flexDirection: 'column', gap: 2 }}>
          {groups.status === 'error' && (
            <div role="status" style={{ padding: '4px 6px 6px', fontSize: 'var(--t-meta)', color: 'var(--rose-fg)', lineHeight: 1.45 }}>
              {t('Groupes de caisses illisibles : {motif}', { motif: groups.message })}
            </div>
          )}
          {shownGroups.length > 0 && (
            <>
              <span style={subTitle}>{t('Groupes de caisses')}</span>
              {shownGroups.map((g) => {
                const here = g.registerIds.filter((r) => layout.registers.includes(r)).length;
                const all = here === g.registerIds.length;
                const some = here > 0 && !all;
                return (
                  <button key={g.id} type="button" className="oz-qs-choice" data-on={all ? '' : undefined} aria-pressed={all ? true : some ? 'mixed' : false}
                    onClick={() => onGroup(g)}
                    style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '7px 8px', borderRadius: 'var(--r-ctrl)', background: all ? 'var(--teal-100)' : 'var(--paper)', textAlign: 'left' }}>
                    <span aria-hidden style={{ width: 16, height: 16, borderRadius: 'var(--r-xs)', border: `1.5px solid ${all || some ? 'var(--teal-500)' : 'var(--ink-300)'}`, background: all ? 'var(--teal-500)' : 'var(--paper)', color: 'var(--paper)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box', flexShrink: 0 }}>
                      {all && <Icon name="check" size={10} stroke={3.4} />}
                      {some && <span style={{ width: 8, height: 2, borderRadius: 1, background: 'var(--teal-500)' }} />}
                    </span>
                    <span aria-hidden style={{ width: 22, height: 22, borderRadius: 'var(--r-xs)', background: 'var(--violet-bg)', color: 'var(--violet-fg)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Icon name="grid" size={12} stroke={2} />
                    </span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: 'block', fontSize: 'var(--t-body)', fontWeight: 700, color: all ? 'var(--teal-800)' : 'var(--ink-700)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{g.name}</span>
                      <span style={{ display: 'block', fontSize: 'var(--t-sub)', color: 'var(--ink-500)', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {all
                          ? t('{liste} — toutes ici', { liste: g.registerIds.map(codeCaisse).join(' · ') })
                          : some
                            ? t('{liste} — {n} sur {v} ici', { liste: g.registerIds.map(codeCaisse).join(' · '), n: here, v: t('{n} caisse', { n: g.registerIds.length }) })
                            : g.registerIds.map(codeCaisse).join(' · ')}
                      </span>
                    </span>
                  </button>
                );
              })}
              <span style={{ ...subTitle, paddingTop: 8 }}>{t('Caisses')}</span>
            </>
          )}
          {registers.length === 0 && (
            <div style={{ padding: '7px 8px', fontSize: 'var(--t-dense)', color: 'var(--ink-500)', lineHeight: 1.45 }}>
              {t('Aucune caisse connue : affectez un appareil au magasin dans la Console, ou re-déclarez le magasin.')}
            </div>
          )}
          {registers.map((r) => {
            const owner = ownerOf(r);
            const on = owner?.id === layout.id;
            return (
              <button key={r} type="button" className="oz-qs-choice" data-on={on ? '' : undefined} aria-pressed={on} onClick={() => onToggle(r)}
                style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '7px 8px', borderRadius: 'var(--r-ctrl)', background: on ? 'var(--teal-100)' : 'var(--paper)', textAlign: 'left' }}>
                <span aria-hidden style={{ width: 16, height: 16, borderRadius: 'var(--r-xs)', border: `1.5px solid ${on ? 'var(--teal-500)' : 'var(--ink-300)'}`, background: on ? 'var(--teal-500)' : 'var(--paper)', color: 'var(--paper)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box', flexShrink: 0 }}>
                  {on && <Icon name="check" size={10} stroke={3.4} />}
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                    <span style={{ fontFamily: MONO, fontSize: 'var(--t-dense)', fontWeight: 700, color: on ? 'var(--teal-800)' : 'var(--ink-700)' }}>{codeCaisse(r)}</span>
                  </span>
                  <span style={{ display: 'block', fontSize: 'var(--t-sub)', color: on ? 'var(--teal-800)' : owner ? 'var(--amber-fg)' : 'var(--ink-400)', marginTop: 1 }}>
                    {on ? t('affichée ici') : owner ? t('dans « {nom} »', { nom: names(owner.id) }) : t('par défaut')}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
        <button type="button" onClick={onDefault} className="oz-qs-menu-row"
          style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 14px', border: 'none', borderTop: '1px solid var(--ink-200)', textAlign: 'left' }}>
          <span style={{ width: 16, height: 16, flexShrink: 0, boxSizing: 'border-box', borderRadius: 'var(--r-pill)', border: '1.5px solid var(--ink-300)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
            {isDefault && <span style={{ width: 8, height: 8, borderRadius: 'var(--r-pill)', background: 'var(--teal-500)' }} />}
          </span>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: 'var(--t-body)', fontWeight: 600, color: 'var(--ink-900)' }}>{t('Disposition par défaut')}</span>
            <span style={{ display: 'block', fontSize: 'var(--t-sub)', color: 'var(--ink-500)', lineHeight: 1.4 }}>{t('Affichée par toute caisse non affectée')}</span>
          </span>
        </button>
        <button type="button" onClick={onHome} className="oz-qs-menu-row"
          style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 14px', border: 'none', borderTop: '1px solid var(--ink-100)', textAlign: 'left' }}>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: 'var(--t-body)', fontWeight: 600, color: 'var(--ink-900)' }}>{t('Retour à l’accueil après ajout')}</span>
            <span style={{ display: 'block', fontSize: 'var(--t-sub)', color: 'var(--ink-500)', lineHeight: 1.4 }}>{t('Chaque article ajouté au ticket ramène à l’écran principal')}</span>
          </span>
          <span aria-hidden style={{ width: 34, height: 20, borderRadius: 'var(--r-pill)', background: layout.settings.home ? 'var(--teal-500)' : 'var(--ink-200)', display: 'flex', alignItems: 'center', padding: 3, justifyContent: layout.settings.home ? 'flex-end' : 'flex-start' }}>
            <span style={{ width: 14, height: 14, borderRadius: 'var(--r-pill)', background: 'var(--paper)' }} />
          </span>
        </button>
        {free.length > 0 && (
          <div style={{ padding: '9px 14px', borderTop: '1px solid var(--ink-100)', background: 'var(--ink-50)', fontSize: 'var(--t-meta)', color: 'var(--ink-600)', lineHeight: 1.5 }}>
            {t('{n} caisses non affectées ({liste}) affichent « {nom} », la disposition par défaut.', { n: free.length, liste: free.map(codeCaisse).join(', '), nom: names(doc.defaultLayoutId) })}
          </div>
        )}
        <div style={{ padding: '10px 14px', borderTop: '1px solid var(--ink-100)' }}>
          <Button size="sm" icon="refresh" disabled={busy} onClick={onResend}>{t('Renvoyer aux caisses')}</Button>
          <span style={{ display: 'block', fontSize: 'var(--t-sub)', color: 'var(--ink-500)', lineHeight: 1.45, marginTop: 6 }}>
            {t('Réémet la disposition publiée et efface les déplacements faits en caisse. Inutile en temps normal.')}
          </span>
        </div>
      </div>
    </>
  );
}

/** Le point d'une disposition : teinté si au moins une caisse l'affiche, gris sinon. */
function LayoutDot({ shown }: { shown: boolean }) {
  return <span style={{ width: 8, height: 8, borderRadius: 'var(--r-pill)', background: shown ? 'var(--teal-500)' : 'var(--ink-300)', flexShrink: 0 }} />;
}

/**
 * Une pastille de la barre « Disposition », comme la maquette : une caisse affectée (teal, chasse fixe), « Toutes les
 * caisses » (la défaut sans caisse affectée : toute caisse l'affiche), ou « PAR DÉFAUT » (gris).
 */
function Chip({ kind, children }: { kind: 'register' | 'all' | 'default'; children: React.ReactNode }) {
  const look: React.CSSProperties = kind === 'register'
    ? { fontFamily: MONO, fontSize: 'var(--t-meta)', fontWeight: 700, background: 'var(--teal-100)', color: 'var(--teal-800)' }
    : kind === 'all'
      ? { fontSize: 'var(--t-note)', fontWeight: 700, background: 'var(--teal-100)', color: 'var(--teal-800)' }
      : { fontSize: 'var(--t-sub)', fontWeight: 800, letterSpacing: '.04em', background: 'var(--ink-100)', color: 'var(--ink-600)' };
  return (
    <span style={{ height: 24, display: 'inline-flex', alignItems: 'center', padding: '0 9px', borderRadius: 'var(--r-pill)', whiteSpace: 'nowrap', flexShrink: 0, ...look }}>
      {children}
    </span>
  );
}

function Banner({ tone, action, children }: { tone: 'amber' | 'rose'; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div role={tone === 'rose' ? 'alert' : 'status'} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', marginBottom: 12, borderRadius: 'var(--r-sm)', background: `var(--${tone}-bg)`, color: `var(--${tone}-fg)`, fontSize: 'var(--t-body)', lineHeight: 1.45 }}>
      <Icon name="alert" size={15} />
      <span style={{ flex: 1, minWidth: 0 }}>{children}</span>
      {action}
    </div>
  );
}


/** La marge du studio : celle de sa maquette (22 px), qui annule la marge de page du back-office. */
const STUDIO_PAD = 22;

/**
 * LA PAGE DU STUDIO — à ses marges à lui.
 * ⚠️ LA MAQUETTE PRINCIPALE EMBARQUE LE STUDIO SOUS `margin: -24px -28px -60px` (la marge de page annulée), et le studio
 *    pose les siennes : 22 px autour, 40 px en bas. Sa grille y gagne 12 px — une colonne de tuiles, parfois.
 */
function Page({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ margin: `-${PAGE_PAD.top}px -${PAGE_PAD.side}px -${PAGE_PAD.bottom}px`, padding: `${STUDIO_PAD}px ${STUDIO_PAD}px 40px` }}>
      {children}
    </div>
  );
}

/** Le titre de la page — gardé par les états de chargement, d'échec et de première visite : on sait où l'on est. */
function TitleBlock() {
  return (
    <>
      <h1 style={{ margin: 0, fontSize: 'var(--t-h1)', fontWeight: 700, letterSpacing: '-0.025em', color: 'var(--ink-900)' }}>{t('Articles rapides')}</h1>
      <p style={{ margin: '4px 0 0', fontSize: 'var(--t-title)', color: 'var(--ink-500)', maxWidth: 760, textWrap: 'pretty' }}>
        <Trans k='Le gérant compose lui-même l’écran de vente : des <1>niveaux</1> de tuiles qui rangent familles, sous-familles et articles. Ce qu’il dessine ici est exactement ce que la caisse affiche.'
          parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} />
      </p>
    </>
  );
}

const subTitle: React.CSSProperties = {
  display: 'block', padding: '4px 6px 3px', fontFamily: MONO, fontSize: 'var(--t-eyebrow)', letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--ink-500)',
};
const menuTitle: React.CSSProperties = {
  padding: '10px 12px 2px', fontFamily: MONO, fontSize: 'var(--t-micro)', letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--ink-500)',
};
